/**
 * The IndexedDB {@link RecordStorePort} — the one implementation every other
 * store in this package is built on.
 *
 * The write path is one ordered sequence and it never varies (architecture
 * §3.4):
 *
 *  1. **Gate** on a *fresh* read. A store whose bytes this build cannot
 *     understand is refused before anything is opened. Mutation is gated, not
 *     attempted-and-rolled-back, so a damaged store is never restored from a
 *     copy that may itself be untrustworthy — and a gate computed from a read
 *     the caller took minutes ago would let a store that has since become
 *     unreadable be overwritten.
 *  2. **Serialize** to canonical text and derive the `index.*` scalars from the
 *     same value in the same step.
 *  3. **Open one `readwrite` transaction.**
 *  4. **Write.**
 *  5. **Re-read inside that transaction** and compare canonical text. A
 *     mismatch aborts. There is no "probably fine" branch.
 *  6. **Commit.**
 *
 * And the read path has exactly three answers — `missing`, `loaded`,
 * `unreadable` — with no helper anywhere that collapses them into
 * `T | undefined`. That helper is how a store the app could not decode becomes
 * a store the app believes is empty, and then overwrites. A user whose history
 * will not decode *has* history.
 */

import type { IDBPDatabase } from "idb";

import type {
  MigrationOutcome,
  MigrationPlan,
  SchemaVersion,
  StoreId,
  StoreReadResult,
  QuarantinedRecord,
  WriteOutcome,
} from "@ff/domain";
import {
  mutationGate as deriveMutationGate,
  planMigration,
  storeDescriptor,
} from "@ff/domain";
import type {
  DecodedRecord,
  KeyedRecord,
  ObservedMutationGate,
  RecordCodec,
  RecordKey,
  RecordMutation,
  RecordStorePort,
  StoreMigrationRegistry,
  StoreMigrationReport,
} from "@ff/domain/ports";

import { CanonicalJsonError } from "./canonicalJson";
import type { PayloadDecoder } from "./codec";
import { canonicalRecordCodec } from "./codec";
import type { RecordDatabase } from "./database";
import type { RecordStoreLayout } from "./layout";
import { recordStoreLayout, resolveRecordKey } from "./layout";
import type { PlannedMutation, RecordIndexer, StoredRow } from "./verifiedWrite";
import {
  applyVerifiedMutations,
  canonicalRow,
  decodeStoredRow,
  planMutations,
} from "./verifiedWrite";

export interface RecordStoreOptions<T> {
  readonly store: StoreId;
  /** The version this build writes. Anything above it is refused, not read. */
  readonly schemaVersion: SchemaVersion;
  readonly database: RecordDatabase;
  /** Overrides the canonical codec. Supplied by tests that need a lossy one. */
  readonly codec?: RecordCodec<T>;
  /** Per-store structural validation (`duplicateIdentity` is separate below). */
  readonly decodePayload?: PayloadDecoder<T>;
  readonly index?: RecordIndexer<T>;
  /**
   * The identity two records may not share. Only consulted for stores whose
   * descriptor lists `duplicateIdentity`, where the identity lives *in the
   * payload* and can therefore collide even though the physical keys cannot.
   */
  readonly identity?: (record: KeyedRecord<T>) => string;
  /** Overrides the layout. Only tests that build a private database need this. */
  readonly layout?: RecordStoreLayout;
}

function noIndexFields(): Readonly<Record<string, never>> {
  return {};
}

export function createRecordStore<T>(
  options: RecordStoreOptions<T>,
): RecordStorePort<T> {
  return new IndexedDbRecordStore(options);
}

/** One row as it came out of IndexedDB, still untrusted. */
interface RawRow {
  readonly key: RecordKey;
  readonly value: unknown;
}

class IndexedDbRecordStore<T> implements RecordStorePort<T> {
  readonly store: StoreId;

  readonly #database: RecordDatabase;
  readonly #layout: RecordStoreLayout;
  readonly #codec: RecordCodec<T>;
  readonly #writeVersion: SchemaVersion;
  readonly #indexer: RecordIndexer<T>;
  readonly #identity: (record: KeyedRecord<T>) => string;
  readonly #quarantines: boolean;
  readonly #detectsDuplicateIdentity: boolean;
  #lastRead: StoreReadResult<readonly KeyedRecord<T>[]> | undefined;

  constructor(options: RecordStoreOptions<T>) {
    const layout = options.layout ?? recordStoreLayout(options.store);
    if (layout === undefined) {
      // `STORE_IDS` is the only authority for what stores exist. A store with
      // no layout would be created by whichever call site guessed first, which
      // is exactly the ad-hoc `createObjectStore` the architecture forbids.
      throw new Error(`no record store layout declared for "${options.store}"`);
    }
    const descriptor = storeDescriptor(options.store);

    this.store = options.store;
    this.#database = options.database;
    this.#layout = layout;
    this.#writeVersion = options.schemaVersion;
    this.#codec =
      options.codec ??
      canonicalRecordCodec<T>({
        currentSchemaVersion: options.schemaVersion,
        ...(options.decodePayload === undefined
          ? {}
          : { decodePayload: options.decodePayload }),
      });
    this.#indexer = options.index ?? noIndexFields;
    this.#identity = options.identity ?? ((record) => record.key);
    // An undeclared store gets the strict policy: no recovery contract has been
    // stated for it, and assuming the forgiving one would quarantine records in
    // a store whose contract may not allow a partial read.
    this.#quarantines = descriptor?.recovery === "perRecordQuarantine";
    this.#detectsDuplicateIdentity =
      descriptor?.failClosedTriggers.includes("duplicateIdentity") ?? false;
  }

  async read(): Promise<StoreReadResult<readonly KeyedRecord<T>[]>> {
    const result = await this.#readAll();
    this.#lastRead = result;
    return result;
  }

  async readRecord(key: RecordKey): Promise<StoreReadResult<T>> {
    const resolved = resolveRecordKey(this.#layout, key);
    let raw: unknown;
    try {
      const database = await this.#database.connection();
      const transaction = database.transaction(this.#name(), "readonly");
      raw = await transaction.store.get(resolved);
      await transaction.done;
    } catch (error) {
      return this.#unreadableFromFailure(error);
    }

    if (raw === undefined) return { status: "missing", store: this.store };

    const decoded = this.#decodeRow({ key: resolved, value: raw });
    if (decoded.status === "undecodable") {
      // Even in a per-record-quarantine store this is `unreadable`, never
      // `missing`: the caller asked for this record, and "it is not there"
      // would be a false answer about a record that is.
      return {
        status: "unreadable",
        store: this.store,
        trigger: decoded.trigger,
        detail: decoded.detail,
        preservedByteLength: decoded.preservedByteLength,
        observedSchemaVersion: decoded.observedSchemaVersion,
      };
    }

    return {
      status: "loaded",
      store: this.store,
      schemaVersion: decoded.schemaVersion,
      value: decoded.value,
      quarantined: [],
    };
  }

  mutationGate(): ObservedMutationGate {
    // `unknown` before the first read is the honest answer, and it is what
    // stops a screen offering Save over a store that will refuse it — the edit
    // would then fail at the last possible moment instead of the first.
    if (this.#lastRead === undefined) return { status: "unknown", store: this.store };
    return {
      status: "known",
      store: this.store,
      gate: deriveMutationGate(this.#lastRead),
    };
  }

  async commit(mutations: readonly RecordMutation<T>[]): Promise<WriteOutcome> {
    const current = await this.read();
    if (current.status === "unreadable") {
      // The bytes this build could not understand are also bytes it will not
      // destroy. This is the whole fail-closed promise, in one branch.
      return { status: "refused", reason: current.trigger };
    }
    if (mutations.length === 0) return { status: "verified" };

    let planned: readonly PlannedMutation[];
    try {
      planned = planMutations(
        this.#layout,
        this.#codec,
        this.#writeVersion,
        this.#indexer,
        mutations,
      );
    } catch (error) {
      if (error instanceof CanonicalJsonError) {
        // The value has no canonical form, so it has no verifiable form either.
        // Refusing names the value; writing it would produce a record that
        // fails its own verification for reasons nobody could trace back here.
        return { status: "refused", reason: "unsupportedShape" };
      }
      throw error;
    }
    const expected = planned[0]?.expectedCanonical ?? "";

    let database: IDBPDatabase;
    try {
      database = await this.#database.connection();
    } catch {
      return { status: "mismatch", expectedCanonical: expected, actualCanonical: null };
    }

    const transaction = database.transaction(this.#name(), "readwrite");
    let outcome: WriteOutcome;
    try {
      outcome = await applyVerifiedMutations(
        transaction.store,
        this.#layout,
        this.#codec,
        planned,
      );
    } catch {
      outcome = { status: "mismatch", expectedCanonical: expected, actualCanonical: null };
    }

    if (outcome.status !== "verified") {
      abort(transaction);
      // An aborted transaction discards *every* write in it, including the ones
      // that verified — which is what makes a partially applied batch
      // impossible rather than merely unlikely.
      await settled(transaction);
      this.#lastRead = await this.#readAll();
      return outcome;
    }

    try {
      await transaction.done;
    } catch {
      return { status: "mismatch", expectedCanonical: expected, actualCanonical: null };
    }

    this.#lastRead = await this.#readAll();
    return { status: "verified" };
  }

  async migrate(registry: StoreMigrationRegistry): Promise<StoreMigrationReport> {
    const current = await this.read();
    if (current.status === "unreadable") {
      return { status: "refused", store: this.store, plan: null, trigger: current.trigger };
    }

    let database: IDBPDatabase;
    try {
      database = await this.#database.connection();
    } catch (error) {
      return {
        status: "refused",
        store: this.store,
        plan: null,
        trigger: this.#unreadableFromFailure(error).trigger,
      };
    }

    const transaction = database.transaction(this.#name(), "readwrite");
    let rows: readonly RawRow[];
    try {
      rows = await readRows(transaction.store, this.#layout);
    } catch (error) {
      abort(transaction);
      await settled(transaction);
      return {
        status: "refused",
        store: this.store,
        plan: null,
        trigger: this.#unreadableFromFailure(error).trigger,
      };
    }

    let lastPlan: MigrationPlan = { kind: "upToDate" };
    const staged: PlannedMutation[] = [];

    for (const row of rows) {
      const decoded = this.#decodeRow(row);
      if (decoded.status === "undecodable") {
        // Reachable only for a quarantining store, whose read above stayed
        // `loaded`. A record that cannot be decoded cannot be migrated, and
        // guessing at its version would rewrite bytes nobody could read.
        continue;
      }

      const plan = planMigration(
        registry.descriptors,
        registry.store,
        decoded.schemaVersion,
        registry.currentSchemaVersion,
      );
      lastPlan = plan;

      if (plan.kind === "refuseFutureSchema" || plan.kind === "unsupportedPath") {
        abort(transaction);
        await settled(transaction);
        return {
          status: "refused",
          store: this.store,
          plan,
          // Distinct on purpose: "written by a newer build" is a reason to tell
          // the user to update, "no registered step out of this version" is a
          // bug in this build.
          trigger:
            plan.kind === "refuseFutureSchema" ? "futureSchema" : "unsupportedShape",
        };
      }
      if (plan.kind === "upToDate") continue;

      const stepped = applyMigrationPath(registry, plan.path, decoded.value);
      if (!stepped.ok) {
        abort(transaction);
        await settled(transaction);
        return { status: "refused", store: this.store, plan, trigger: stepped.trigger };
      }

      const migrated: StoredRow = {
        key: row.key,
        schemaVersion: registry.currentSchemaVersion,
        payload: this.#codec.encode({
          schemaVersion: registry.currentSchemaVersion,
          payload: stepped.value,
        }),
        index: this.#indexer(stepped.value),
      };
      staged.push({
        key: row.key,
        row: migrated,
        expectedCanonical: canonicalRow(migrated),
      });
    }

    if (staged.length === 0) {
      // Nothing to write. Committing an empty transaction is harmless, and
      // aborting it would report a failure that did not happen.
      await settled(transaction);
      return {
        status: "completed",
        store: this.store,
        plan: lastPlan,
        migratedRecords: 0,
      };
    }

    let outcome: WriteOutcome;
    try {
      outcome = await applyVerifiedMutations(
        transaction.store,
        this.#layout,
        this.#codec,
        staged,
      );
    } catch {
      outcome = { status: "mismatch", expectedCanonical: "", actualCanonical: null };
    }

    if (outcome.status !== "verified") {
      abort(transaction);
      await settled(transaction);
      this.#lastRead = await this.#readAll();
      // Every step is pure and nothing was committed, so the pass can simply be
      // run again once the cause is fixed.
      return { status: "refused", store: this.store, plan: lastPlan, trigger: "corruptBytes" };
    }

    try {
      await transaction.done;
    } catch {
      return { status: "refused", store: this.store, plan: lastPlan, trigger: "corruptBytes" };
    }

    this.#lastRead = await this.#readAll();
    return {
      status: "completed",
      store: this.store,
      plan: lastPlan,
      migratedRecords: staged.length,
    };
  }

  /* --- internals --------------------------------------------------------- */

  #name(): string {
    return this.store;
  }

  #decodeRow(raw: RawRow): DecodedRecord<T> {
    const row = decodeStoredRow(raw.value, raw.key);
    if (row.status === "unusable") {
      return {
        status: "undecodable",
        trigger: row.trigger,
        detail: row.detail,
        observedSchemaVersion: row.observedSchemaVersion,
        preservedByteLength: row.preservedByteLength,
      };
    }
    return this.#codec.decode(row.row.payload);
  }

  async #readAll(): Promise<StoreReadResult<readonly KeyedRecord<T>[]>> {
    let rows: readonly RawRow[];
    try {
      const database = await this.#database.connection();
      const transaction = database.transaction(this.#name(), "readonly");
      rows = await readRows(transaction.store, this.#layout);
      await transaction.done;
    } catch (error) {
      return this.#unreadableFromFailure(error);
    }

    if (rows.length === 0) return { status: "missing", store: this.store };

    const records: KeyedRecord<T>[] = [];
    const quarantined: QuarantinedRecord[] = [];
    let lowestVersion: SchemaVersion | undefined;

    for (const row of rows) {
      const decoded = this.#decodeRow(row);
      if (decoded.status === "undecodable") {
        if (!this.#quarantines) {
          // One bad history record closes history rather than silently
          // shortening it. The bytes stay exactly where they are.
          return {
            status: "unreadable",
            store: this.store,
            trigger: decoded.trigger,
            detail: `${row.key}: ${decoded.detail}`,
            preservedByteLength: decoded.preservedByteLength,
            observedSchemaVersion: decoded.observedSchemaVersion,
          };
        }
        // One bad Journal entry must not close the Journal. The item carries
        // metadata only — the content is precisely what could not be trusted to
        // decode.
        quarantined.push({
          recordKey: row.key,
          trigger: decoded.trigger,
          detail: decoded.detail,
          preservedByteLength: decoded.preservedByteLength,
        });
        continue;
      }
      records.push({ key: row.key, value: decoded.value });
      // The *lowest* version present is the one a migration has to start from;
      // reporting the highest would hide a record that is still behind.
      if (lowestVersion === undefined || decoded.schemaVersion < lowestVersion) {
        lowestVersion = decoded.schemaVersion;
      }
    }

    const duplicate = this.#findDuplicateIdentity(records);
    if (duplicate !== undefined) {
      // The app stops rather than choose between two records claiming one
      // identity. Choosing would silently discard whichever it did not pick.
      return {
        status: "unreadable",
        store: this.store,
        trigger: "duplicateIdentity",
        detail: duplicate,
        preservedByteLength: preservedBytes(rows),
        observedSchemaVersion: lowestVersion ?? null,
      };
    }

    return {
      status: "loaded",
      store: this.store,
      schemaVersion: lowestVersion ?? this.#writeVersion,
      value: records,
      quarantined,
    };
  }

  #findDuplicateIdentity(records: readonly KeyedRecord<T>[]): string | undefined {
    if (!this.#detectsDuplicateIdentity) return undefined;
    const seen = new Map<string, RecordKey>();
    for (const record of records) {
      const identity = this.#identity(record);
      const first = seen.get(identity);
      if (first !== undefined) {
        return `records "${first}" and "${record.key}" both claim identity "${identity}"`;
      }
      seen.set(identity, record.key);
    }
    return undefined;
  }

  #unreadableFromFailure(
    error: unknown,
  ): Extract<StoreReadResult<never>, { readonly status: "unreadable" }> {
    return {
      status: "unreadable",
      store: this.store,
      // `FailClosedTrigger` has no case for "the database would not open", and
      // inventing one here would be a contract change. `corruptBytes` is the
      // trigger whose recovery copy is true either way: nothing was changed or
      // deleted, and the data was left exactly as found.
      trigger: "corruptBytes",
      detail: describeFailure(error),
      preservedByteLength: 0,
      observedSchemaVersion: null,
    };
  }
}

/* --------------------------------------------------------------------------- *
 * Shared helpers
 * --------------------------------------------------------------------------- */

async function readRows(
  store: { getAll(): Promise<unknown[]>; getAllKeys(): Promise<IDBValidKey[]> },
  layout: RecordStoreLayout,
): Promise<readonly RawRow[]> {
  const values = await store.getAll();
  const keys = await store.getAllKeys();
  const rows: RawRow[] = [];
  for (let position = 0; position < values.length; position += 1) {
    const key = keys[position];
    rows.push({
      key:
        layout.keying.kind === "singleton"
          ? layout.keying.key
          : typeof key === "string"
            ? key
            : String(key),
      value: values[position],
    });
  }
  return rows;
}

function preservedBytes(rows: readonly RawRow[]): number {
  let total = 0;
  for (const row of rows) {
    const decoded = decodeStoredRow(row.value, row.key);
    total +=
      decoded.status === "row" ? decoded.row.payload.length : decoded.preservedByteLength;
  }
  return total;
}

function applyMigrationPath<T>(
  registry: StoreMigrationRegistry,
  path: readonly SchemaVersion[],
  value: T,
): MigrationOutcome<T> {
  let carried: unknown = value;
  for (let step = 0; step + 1 < path.length; step += 1) {
    const from = path[step];
    const to = path[step + 1];
    if (from === undefined || to === undefined) break;
    const descriptor = registry.descriptors.find(
      (candidate) =>
        candidate.store === registry.store &&
        candidate.from === from &&
        candidate.to === to,
    );
    if (descriptor === undefined) {
      return { ok: false, trigger: "unsupportedShape", detail: `no step ${from}→${to}` };
    }
    const outcome = descriptor.migrate(carried);
    if (!outcome.ok) return outcome;
    carried = outcome.value;
  }
  // The registry erases its payload types (`planMigration` takes
  // `MigrationDescriptor<unknown, unknown>[]`), so the store re-asserts the type
  // it was constructed with. This is the erasure being undone, not a widening.
  return { ok: true, value: carried as T };
}

function abort(transaction: { abort(): void }): void {
  try {
    transaction.abort();
  } catch {
    // Already aborted or already finished; either way there is nothing left to
    // undo, and throwing here would replace a precise write outcome with a
    // meaningless one.
  }
}

async function settled(transaction: { done: Promise<unknown> }): Promise<void> {
  try {
    await transaction.done;
  } catch {
    // An aborted transaction rejects `done` by design. The rejection is the
    // abort being confirmed, not a second failure.
  }
}

function describeFailure(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  return String(error);
}
